import { useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Types
import { GoalItem, GoalMetricTypeType } from 'Shared/constants/types';
import { Comment } from 'Shared/types/Comment.type';
import { Workspace } from 'Shared/types/Workspace.type';

// Actions
import * as goalsActions from 'Shared/actions/Goals.actions';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useCurrentUser } from './use-current-user';
import { useUser } from 'Shared/utils/hooks/use-user';
import { useDeepEqualSelector } from 'Shared/utils/hooks';

// Selectors
import { getGoalBySlug, getGoalsByParentId, getGoalById } from 'Shared/selectors/Goals.selectors';
import * as CommentsSelectors from 'Shared/selectors/Comments.selectors';
import { getWorkspaceDisplayLabel } from 'Shared/selectors/workspace';
import { getActionItems } from 'Shared/selectors/meetingItems';

type Props = {
  slug: string;
  fetchOnMount?: boolean;
};

export const useGoal = ({ slug, fetchOnMount = false }: Props) => {
  const dispatch = useDispatch();

  const [isFetchingGoal, setIsFetchingGoal] = useState<boolean>(fetchOnMount);
  const [isSaving, toggleIsSaving] = useToggle(false);
  const [hasError, setHasError] = useState<boolean>(false);

  const goal: GoalItem = useSelector(state => getGoalBySlug(state, { slug }))?.toJS() || null;
  const parentGoal: GoalItem = useSelector(state => getGoalById(state, { id: goal?.parentId })?.toJS());

  const subGoals: Array<GoalItem> =
    useSelector(state => getGoalsByParentId(state, { parentId: goal?.id }))
      ?.valueSeq()
      ?.toJS() || [];

  const comments: Comment[] = useSelector(state =>
    CommentsSelectors.getCommentsByParentTypeAndParentId(state, { parentType: 'goal', parentId: slug })
  );

  const actionItems = useDeepEqualSelector(state => {
    return getActionItems(state).filter(({ goalSlug: meetingItemGoalSlug }) => meetingItemGoalSlug === slug);
  });

  const workspaceLabel = useSelector(getWorkspaceDisplayLabel(goal?.workspaceSlug));
  const workspace: Workspace = useSelector(state => state.workspaces.find(({ id }) => id === goal?.workspaceSlug));

  const { id: currentUserId } = useCurrentUser();
  const { user: completedBy } = useUser({ id: goal?.completedById }) || null;
  const { user: owner } = useUser({ id: goal?.assigneeId }) || null;

  const isComplete = goal?.complete;
  const isReadOnly = !goal?.canEditActionItems;
  const canChangeStatus = currentUserId === goal?.assignerId || currentUserId === goal?.assigneeId;
  const isProgressActionItems = goal?.metricType === GoalMetricTypeType.action_items;
  const isProgressSubGoals = goal?.metricType === GoalMetricTypeType.sub_goals;
  const isProgressNumber = goal?.metricType === GoalMetricTypeType.number;
  const isProgressPercentage = goal?.metricType === GoalMetricTypeType.percent;
  const isProgressDollarAmount = goal?.metricType === GoalMetricTypeType.dollar_amount;

  const handleMarkComplete = ({
    closeActionItems = false,
    resolutionNote = '',
  }: {
    closeActionItems: boolean;
    resolutionNote: string;
  }) => {
    toggleIsSaving();
    dispatch(goalsActions.markGoalComplete({ closeActionItems, goalSlug: slug, resolutionNote })).then(response => {
      toggleIsSaving();
      return response;
    });
  };

  const handleMarkIncomplete = useCallback(async () => {
    toggleIsSaving();
    dispatch(goalsActions.markGoalIncomplete(slug)).then(response => {
      toggleIsSaving();
      return response;
    });
  }, [dispatch, slug, toggleIsSaving]);

  const handleUpdateGoal = useCallback(
    data => {
      toggleIsSaving();
      return dispatch(goalsActions.updateGoal({ goal: { slug, ...data } }, false)).then(response => {
        toggleIsSaving();
        return response;
      });
    },
    [dispatch, slug, toggleIsSaving]
  );

  const handleDeleteGoal = () => {
    toggleIsSaving();
    return dispatch(goalsActions.deleteGoal(slug)).then(response => {
      toggleIsSaving();
      return response;
    });
  };

  useEffect(() => {
    if (fetchOnMount && slug) {
      setIsFetchingGoal(true);
      dispatch(goalsActions.fetchSingleGoal(slug))
        .then(response => {
          if (!response) setHasError(true);
          setIsFetchingGoal(false);
        })
        .catch(() => {
          setHasError(true);
          setIsFetchingGoal(false);
        });
    }
  }, [slug, fetchOnMount]);

  return {
    goal,
    parentGoal,
    subGoals,
    hasError,
    comments,
    isSaving,
    isReadOnly,
    isProgressActionItems,
    isProgressSubGoals,
    isProgressNumber,
    isProgressPercentage,
    isProgressDollarAmount,
    isFetchingGoal,
    isComplete,
    canChangeStatus,
    completedBy,
    workspace,
    workspaceLabel,
    owner,
    handleUpdateGoal,
    handleMarkComplete,
    handleMarkIncomplete,
    handleDeleteGoal,
    actionItems,
  };
};
